import { useEffect, RefObject } from 'react';

import type Handsontable from 'handsontable';

/**
 * Custom hook to handle click outside events to close popup
 * @param popupRef - Ref to the popup element
 * @param cellElement - The cell element that should not trigger close
 * @param onClose - Callback function to close the popup
 * @param eventType - Event type to listen to ('mousedown' or 'click'), default is 'mousedown'
 */
export const useClickOutside = (
  popupRef: RefObject<HTMLElement | null>,
  cellElement: HTMLElement | null | undefined,
  onClose: () => void,
  eventType: 'mousedown' | 'click' = 'mousedown'
): void => {
  useEffect(() => {
    if (!cellElement) {
      return;
    }

    const handleClickOutside = (e: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(e.target as Node) &&
        !cellElement.contains(e.target as Node)
      ) {
        onClose();
      }
    };

    // Use setTimeout to avoid immediate trigger
    const timeoutId = setTimeout(() => {
      document.addEventListener(eventType, handleClickOutside, true);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener(eventType, handleClickOutside, true);
    };
  }, [popupRef, cellElement, onClose, eventType]);
};

/**
 * Custom hook to handle click outside component to deselect Handsontable
 * @param componentRef - Ref to the component container element
 * @param hotTableRef - Ref to the Handsontable instance
 * @param eventType - Event type to listen to ('mousedown' or 'click'), default is 'mousedown'
 */
export const useClickOutComponent = (
  componentRef: RefObject<HTMLElement | null>,
  hotTableRef: RefObject<Handsontable | null>,
  eventType: 'mousedown' | 'click' = 'mousedown'
): void => {
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const hot = hotTableRef.current;
      if (!hot || !componentRef.current) {
        return;
      }

      const target = e.target as Node;

      // Check if Handsontable context menu is open
      // Handsontable context menu has class 'htContextMenu' or 'htDropdownMenu'
      const contextMenu = document.querySelector('.htContextMenu, .htDropdownMenu');
      const isContextMenuOpen =
        contextMenu && contextMenu instanceof HTMLElement && contextMenu.offsetParent !== null; // Check if visible

      // Check if click is inside context menu
      const isClickInContextMenu = contextMenu?.contains(target);

      // Don't deselect if context menu is open or click is inside context menu
      if (isContextMenuOpen || isClickInContextMenu) {
        return;
      }

      // Check if click is outside the component container
      if (!componentRef.current.contains(target)) {
        // Deselect all cells in Handsontable
        hot.deselectCell();
      }
    };

    // Use setTimeout to avoid immediate trigger
    const timeoutId = setTimeout(() => {
      document.addEventListener(eventType, handleClickOutside, true);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener(eventType, handleClickOutside, true);
    };
  }, [componentRef, hotTableRef, eventType]);
};
